import { createContext, useContext, useState } from 'react';
import { useOtherInfo } from '@moko/utils';
import { isContractorRole } from '@moko/utils/src/authHelper';
import type { IOtherInfoProps } from './types';

interface OtherInfoProviderProps {
  params: IOtherInfoProps;
  rerenderOtherInfo: () => void;
  children: React.ReactNode;
}

export const OtherInfoProvider = ({
  params,
  rerenderOtherInfo,
  children,
}: OtherInfoProviderProps) => {
  const [formAreaWidth, setFormAreaWidth] = useState(0);
  const otherInfoHook = useOtherInfo({
    metadata: params.metadata,
    otherInfo: params.otherInfo,
    isSaved: params.isSaved,
    validationErrors: params.validationErrors,
    onOtherInfoChanged: params.onOtherInfoChanged,
    onValidationErrorsChanged: params.onValidationErrorsChanged,
    relatedEntities: params.relatedEntities,
    isContractorRole: isContractorRole(),
  });

  const contextValue = {
    ...otherInfoHook,
    params,
    rerenderOtherInfo,
    formAreaWidth,
    setFormAreaWidth,
    unansweredOnly: params.unansweredOnly ?? false,
  };

  return (
    <OtherInfoContext.Provider value={contextValue}>
      {children}
    </OtherInfoContext.Provider>
  );
};

type OtherInfoContextValue = ReturnType<typeof useOtherInfo> & {
  params: IOtherInfoProps;
  rerenderOtherInfo: () => void;
  formAreaWidth: number;
  setFormAreaWidth: (width: number) => void;
  unansweredOnly: boolean;
};

const OtherInfoContext = createContext<OtherInfoContextValue | null>(null);

export const useOptionalOtherInfoContext = () => useContext(OtherInfoContext);

export const useOtherInfoContext = (): OtherInfoContextValue => {
  const context = useOptionalOtherInfoContext();
  if (!context) {
    throw new Error(
      'useOtherInfoContext must be used within OtherInfoProvider'
    );
  }
  return context;
};
